import { Box, Container, Paper, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchAPI } from '../../../api/request';
import Alert from '../../../components/common/Alert.component';
import Back from '../../../components/common/Back.component';
import Loading from '../../../components/common/Loading.component';
import AddView from '../../../views/Add.view';
import { API as departments } from '../../../module/departments/api';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import AgentNAForm from '../../../components/agentNA/AgentNAForm.component';
import { API as yachts } from '../../../module/yachts/api';
import DepotNewYachtForm from '../../../components/yacht/DepotNewYachtForm.component';
import dayjs from 'dayjs';

const DepotAddPage = ({ module: { code, query, tag, create } }) => {
  const navigate = useNavigate();
  const { jwt, user } = useSelector((state) => state.login);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [idDepartment, setIdDepartment] = useState(null);
  const [error, setError] = useState(false);
  const [valueTab, setValueTab] = useState(0);
  const [conf, setConf] = useState(create.conf?.DEFAULT);

  const userInCharge = useMemo(
    () =>
      user && user?.id_users_nautica?.id
        ? {
            id: user?.id_users_nautica?.id,
            name: `${user?.name} ${user?.surname}`,
          }
        : null,
    [user]
  );

  const handleChangeTab = (event, newValue) => {
    setValueTab(newValue);
  };

  const onError = useCallback((error) => {
    setSuccess(false);
    setLoading(false);
    setError(true);
    // eslint-disable-next-line
  }, []);

  const onSuccess = useCallback(() => {
    setError(false);
    setSuccess(true);
    setLoading(false);
  }, [setSuccess, setLoading, setError]);

  const onSubmit = useCallback(
    (values) => {
      setSuccess(false);
      setLoading(true);
      setError(false);
      const newYacht = {
        yacht_name: values?.yacht_name,
        loa: values?.loa ? values?.loa : 0,
        beam: values?.beam ? values?.beam : 0,
        draft: values?.draft ? values?.draft : 0,
        gross_tonn: values?.gross_tonn ? values?.gross_tonn : 0,
        yacht_type: values?.yacht_type?.id,
        date_submit_signature: dayjs().format('YYYY-MM-DD'),
        registration_status: 'PENDING',
        registry_type: null,
        port_of_registry: null,
      };
      const dataCommon = {
        ...values,
        id_department: idDepartment,
        id_user_last_updated: user?.id_users_nautica?.id || null,
      };
      const data =
        valueTab !== 0
          ? {
              ...dataCommon,
              ...newYacht,
            }
          : dataCommon;

      fetchAPI(query.create.settings, {}, '', { ...data }, jwt)
        .then((response) => {
          const id_operation = response?.data?.id;
          const id_list = response?.data?.attributes?.id_list?.data?.id;
          setError(false);
          setSuccess(true);
          setLoading(false);
          navigate(`/department/depot/${id_operation}/list/${id_list}`, {
            replace: true,
            state: { id_operation: `${id_operation}` },
          });
        })
        .catch(onError);
    },
    [setLoading, jwt, onError, idDepartment, query, valueTab, user, navigate]
  );

  useEffect(() => {
    fetchAPI(departments.select, { code: code }, '', null, jwt)
      .then((response) => {
        setIdDepartment(_.head(response)?.id);
      })
      .catch(onError);
  }, [code, jwt, onError]);

  useEffect(() => {
    if (valueTab !== 0) {
      setConf(create.conf?.NEW_YACHT);
    } else {
      setConf(create.conf?.DEFAULT);
    }
  }, [valueTab, create]);

  return (
    <Container disableGutters={true} maxWidth={false} sx={{ height: '100%' }}>
      <Stack
        spacing={2}
        sx={{ padding: 2 }}
        justifyContent='center'
        alignItems='center'
      >
        <Back title={tag} />
        <Container>
          {loading && <Loading />}
          {success && <Alert severity='success'>{'Success: Add'}</Alert>}
          {error && <Alert severity='error'>{'An error has occurred'}</Alert>}

          <Paper variant='outlined'>
            <Box sx={{ padding: 2 }}>
              {idDepartment && (
                <AddView
                  conf={{
                    ...conf,
                    defaultValues: {
                      ...conf?.defaultValues,
                      id_user_in_charge: userInCharge,
                    },
                  }}
                  disabled={loading}
                  onAddSubmit={onSubmit}
                  onAddError={onError}
                  onAddSuccess={onSuccess}
                  actions={
                    <>
                      <DepotNewYachtForm
                        api={yachts.selectNoDRAFT}
                        valueTab={valueTab}
                        handleChange={handleChangeTab}
                      />
                      {valueTab === 0 && <AgentNAForm />}
                    </>
                  }
                />
              )}
            </Box>
          </Paper>
        </Container>
      </Stack>
    </Container>
  );
};

export default DepotAddPage;
